// The tab icon with a small red badge while critical alerts are open (drawn on a canvas); the original icons
// come back at 0. Nothing happens where canvas or the icon image is unavailable.

import { FAVICON_BADGE as B } from '@constants/layout';

const originals = new Map<HTMLLinkElement, string>();
let source: Promise<HTMLImageElement> | null = null;
let generation = 0;

const icons = () => [...document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')];

function loadSource(href: string) {
  source ??= new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      source = null; // the next count tries again
      reject(new Error('icon not loadable'));
    };
    img.src = href;
  });
  return source;
}

/** Badge with the count (one digit fits on a tab; more shows "9+"), 0 restores the original icons. */
export async function setFaviconBadge(n: number) {
  const my = ++generation;
  const links = icons();
  for (const l of links) if (!originals.has(l)) originals.set(l, l.href);
  if (n <= 0) {
    for (const [l, href] of originals) l.href = href;
    return;
  }
  // the largest original is the sharpest base for the badge
  const px = (l: HTMLLinkElement) => parseInt(l.sizes.value, 10) || 0;
  const base = [...originals.entries()].sort((a, b) => px(b[0]) - px(a[0]))[0]?.[1];
  if (!base) return;
  try {
    const img = await loadSource(base);
    if (my !== generation) return; // a newer count came in meanwhile
    const canvas = document.createElement('canvas');
    canvas.width = B.size;
    canvas.height = B.size;
    const g = canvas.getContext('2d');
    if (!g) return;
    const css = getComputedStyle(document.documentElement);
    const red = css.getPropertyValue('--red').trim() || 'red';
    const white = css.getPropertyValue('--surface').trim() || 'white';
    g.drawImage(img, 0, 0, B.size, B.size);
    const r = B.radius;
    const cx = B.size - r;
    const cy = r;
    g.beginPath();
    g.arc(cx, cy, r, 0, Math.PI * 2);
    g.fillStyle = white;
    g.fill();
    g.beginPath();
    g.arc(cx, cy, r - B.ring, 0, Math.PI * 2);
    g.fillStyle = red;
    g.fill();
    g.fillStyle = white;
    g.font = `800 ${n > 9 ? B.fontSmall : B.font}px ${css.getPropertyValue('--font').trim() || 'sans-serif'}`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(n > 9 ? '9+' : String(n), cx, cy + 1);
    const url = canvas.toDataURL('image/png');
    for (const l of links) l.href = url;
  } catch {
    /* icon not loadable: the tab title still carries the count */
  }
}
